<template>
  <div class="dashboard">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">数据概览</h2>
      <p class="page-subtitle">实时监控平台运营数据和关键指标</p>
    </div>

    <!-- 数据统计卡片 -->
    <div class="stats-grid">
      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon" style="color: #409EFF;">
            <el-icon><User /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.totalUsers || 0 }}</div>
            <div class="stat-label">总用户数</div>
          </div>
          <div class="stat-trend" v-if="stats.userGrowth > 0">
            <el-icon color="#67C23A"><Top /></el-icon>
            <span>+{{ stats.userGrowth }}%</span>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon" style="color: #67C23A;">
            <el-icon><Calendar /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.totalActivities || 0 }}</div>
            <div class="stat-label">总活动数</div>
          </div>
          <div class="stat-trend" v-if="stats.activityGrowth > 0">
            <el-icon color="#67C23A"><Top /></el-icon>
            <span>+{{ stats.activityGrowth }}%</span>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon" style="color: #E6A23C;">
            <el-icon><Trophy /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.totalTeams || 0 }}</div>
            <div class="stat-label">总战队数</div>
          </div>
          <div class="stat-trend" v-if="stats.teamGrowth > 0">
            <el-icon color="#67C23A"><Top /></el-icon>
            <span>+{{ stats.teamGrowth }}%</span>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon" style="color: #9B59B6;">
            <el-icon><Flag /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.totalTeamParticipants || 0 }}</div>
            <div class="stat-label">战队参赛次数</div>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon" style="color: #F56C6C;">
            <el-icon><Money /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">¥{{ (Number(stats.totalRevenue) || 0).toFixed(2) }}</div>
            <div class="stat-label">平台净收入</div>
          </div>
          <div class="stat-trend" v-if="stats.revenueGrowth > 0">
            <el-icon color="#67C23A"><Top /></el-icon>
            <span>+{{ stats.revenueGrowth }}%</span>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 图表区域 -->
    <div class="charts-grid">
      <!-- 用户增长趋势图 -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>用户增长趋势</span>
            <el-select v-model="userChartRange" size="small" style="width: 120px;">
              <el-option label="近7天" value="7" />
              <el-option label="近30天" value="30" />
              <el-option label="近90天" value="90" />
            </el-select>
          </div>
        </template>
        <div class="chart-container">
          <div v-if="userChartData.length === 0" class="chart-placeholder">
            <el-empty description="暂无数据" />
          </div>
          <div v-else class="chart-content">
            <!-- ECharts用户增长趋势图 -->
            <VueECharts 
              :option="userChartOption" 
              :autoresize="true"
              style="height: 100%; width: 100%;"
            />
          </div>
        </div>
      </el-card>

      <!-- 活动类型分布 -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>活动类型分布</span>
          </div>
        </template>
        <div class="chart-container">
          <div v-if="activityTypeData.length === 0" class="chart-placeholder">
            <el-empty description="暂无数据" />
          </div>
          <div v-else class="chart-content">
            <div class="type-distribution">
              <div 
                v-for="(item, index) in activityTypeData" 
                :key="index"
                class="type-item"
              >
                <div class="type-info">
                  <div class="type-color" :style="{ backgroundColor: item.color }"></div>
                  <span class="type-name">{{ item.name }}</span>
                </div>
                <div class="type-value">{{ item.value }}场</div>
                <div class="type-percent">{{ item.percent }}%</div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 最近活动和用户 -->
    <div class="recent-grid">
      <!-- 最近活动 -->
      <el-card class="recent-card">
        <template #header>
          <div class="recent-header">
            <span>最近活动</span>
            <el-button link @click="$router.push('/activities')">查看全部</el-button>
          </div>
        </template>
        <div class="recent-content">
          <div v-if="recentActivities.length === 0" class="empty-recent">
            <el-empty description="暂无活动" />
          </div>
          <div v-else>
            <div 
              v-for="activity in recentActivities" 
              :key="activity.id"
              class="recent-item"
            >
              <div class="item-main">
                <div class="item-title">{{ activity.title }}</div>
                <div class="item-meta">
                  <span class="meta-item">
                    <el-icon><Calendar /></el-icon>
                    {{ activity.time }}
                  </span>
                  <span class="meta-item">
                    <el-icon><Location /></el-icon>
                    {{ activity.location }}
                  </span>
                </div>
              </div>
              <div class="item-status">
                <el-tag 
                  :type="getStatusType(activity.status)"
                  size="small"
                >
                  {{ getStatusLabel(activity.status) }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 最近用户 -->
      <el-card class="recent-card">
        <template #header>
          <div class="recent-header">
            <span>最近注册用户</span>
            <el-button link @click="$router.push('/users')">查看全部</el-button>
          </div>
        </template>
        <div class="recent-content">
          <div v-if="recentUsers.length === 0" class="empty-recent">
            <el-empty description="暂无用户" />
          </div>
          <div v-else>
            <div 
              v-for="user in recentUsers" 
              :key="user.id"
              class="recent-item"
            >
              <div class="avatar-container">
                <el-avatar :size="40" :src="user.avatar" />
                <div class="user-info">
                  <div class="user-name">{{ user.name }}</div>
                  <div class="user-meta">
                    {{ user.phone }} · {{ user.registerTime }}
                  </div>
                </div>
              </div>
              <div class="user-status">
                <el-tag 
                  :type="user.vip ? 'success' : 'info'"
                  size="small"
                >
                  {{ user.vip ? 'VIP' : '普通' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Flag } from '@element-plus/icons-vue'
import api from '@/utils/api'
import * as echarts from 'echarts'
import VueECharts from 'vue-echarts'

export default {
  name: 'Dashboard',
  
  components: {
    VueECharts,
    Flag
  },
  
  setup() {
    const stats = ref({
      totalUsers: 0,
      totalActivities: 0,
      totalTeams: 0,
      totalTeamParticipants: 0,
      totalRevenue: 0,
      userGrowth: 0,
      activityGrowth: 0,
      teamGrowth: 0,
      revenueGrowth: 0
    })
    const userChartRange = ref('7')
    const recentActivities = ref([])
    const recentUsers = ref([])
    
    // 用户增长数据
    const userChartData = ref([])
    
    // 活动类型数据
    const activityTypeData = ref([])
    
    // ECharts图表配置
    const userChartOption = computed(() => {
      if (userChartData.value.length === 0) {
        return {
          title: {
            text: '暂无数据',
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#909399',
              fontSize: 14
            }
          }
        }
      }
      
      // 提取日期和用户数数据
      const dates = userChartData.value.map(item => item.date)
      const values = userChartData.value.map(item => item.value)
      
      return {
        title: {
          text: `用户增长趋势（${userChartRange.value}天）`,
          left: 'center',
          textStyle: {
            color: '#303133',
            fontSize: 14
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            const data = params[0]
            return `${data.name}<br/>新增用户：${data.value}人`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            color: '#606266',
            fontSize: 12
          },
          axisLine: {
            lineStyle: {
              color: '#DCDFE6'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#606266',
            fontSize: 12,
            formatter: function(value) {
              // 确保只显示整数
              return Math.floor(value) === value ? value : ''
            }
          },
          axisLine: {
            lineStyle: {
              color: '#DCDFE6'
            }
          },
          splitLine: {
            lineStyle: {
              color: '#EBEEF5',
              type: 'dashed'
            }
          },
          // 设置最小值为0，确保用户数从0开始
          min: 0,
          // 设置刻度间隔为1，确保只显示整数刻度
          interval: 1
        },
        series: [
          {
            name: '新增用户',
            type: 'bar',
            data: values,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#409EFF' },
                { offset: 1, color: '#79BBFF' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#3375FF' },
                  { offset: 1, color: '#66A3FF' }
                ])
              }
            }
          }
        ]
      }
    })
    
    // 计算最大用户值用于图表高度
    const maxUserValue = computed(() => {
      return Math.max(...userChartData.value.map(item => item.value), 0)
    })
    
    // 获取状态类型
    const getStatusType = (status) => {
      const types = {
        '进行中': 'success',
        '未开始': 'info',
        '已结束': 'warning',
        '已取消': 'danger',
        '待审核': 'primary',
        'published': 'info',
        'ongoing': 'success',
        'completed': 'warning',
        'cancelled': 'danger'
      }
      return types[status] || 'info'
    }
    
    // 获取状态标签（英文转中文）
    const getStatusLabel = (status) => {
      const statusMap = {
        'published': '已发布',
        'ongoing': '进行中',
        'completed': '已完成',
        'cancelled': '已取消'
      }
      return statusMap[status] || status
    }
    
    // 加载统计数据
    const loadStats = async () => {
      try {
        // 并行加载所有数据，使用 allSettled 避免单个API失败导致全部失败
        const results = await Promise.allSettled([
          api.getDashboardData(),
          api.getUserGrowth(),
          api.getActivityTypes(),
          api.getRecentActivities(),
          api.getRecentUsers()
        ])
        
        const [statsRes, userGrowthRes, activityTypesRes, recentActivitiesRes, recentUsersRes] = results.map(r => 
          r.status === 'fulfilled' ? r.value : null
        )
        
        // 设置真实数据，确保数据结构正确
        stats.value = statsRes?.data || {
          totalUsers: 0,
          totalActivities: 0,
          totalTeams: 0,
          totalTeamParticipants: 0,
          totalRevenue: 0,
          userGrowth: 0,
          activityGrowth: 0,
          teamGrowth: 0,
          revenueGrowth: 0
        }
        userChartData.value = userGrowthRes?.data || []
        activityTypeData.value = activityTypesRes?.data || []
        recentActivities.value = recentActivitiesRes?.data || []
        recentUsers.value = recentUsersRes?.data || []
        
        // 如果有任何一个请求失败，显示警告（但不阻塞页面）
        const failedApis = results.filter(r => r.status === 'rejected')
        if (failedApis.length > 0 && failedApis.length < results.length) {
          console.warn(`${failedApis.length}个数据接口加载失败:`, failedApis.map(r => r.reason?.response?.data?.message || r.reason?.message))
        } else if (failedApis.length === results.length) {
          const errMsg = failedApis[0]?.reason?.response?.data?.message || failedApis[0]?.reason?.message || '未知错误'
          ElMessage.error('加载数据失败: ' + errMsg)
        }
        
      } catch (error) {
        console.error('加载统计数据失败:', error)
        ElMessage.error('加载数据失败')
        
        // 设置默认值，防止页面崩溃
        stats.value = {
          totalUsers: 0,
          totalActivities: 0,
          totalTeams: 0,
          totalTeamParticipants: 0,
          totalRevenue: 0,
          userGrowth: 0,
          activityGrowth: 0,
          teamGrowth: 0,
          revenueGrowth: 0
        }
        userChartData.value = []
        activityTypeData.value = []
        recentActivities.value = []
        recentUsers.value = []
      }
    }
    
    // 监听图表范围变化
    const watchChartRange = () => {
      // 根据选择的时间范围重新加载用户增长数据
      loadUserGrowthData()
    }
    
    // 加载用户增长数据
    const loadUserGrowthData = async () => {
      try {
        const response = await api.getUserGrowth(userChartRange.value)
        userChartData.value = response?.data || []
      } catch (error) {
        console.error('加载用户增长数据失败:', error)
        ElMessage.error('加载用户增长数据失败')
      }
    }
    
    // 监听时间范围变化
    watch(userChartRange, (newRange) => {
      if (newRange) {
        loadUserGrowthData()
      }
    })
    
    onMounted(() => {
      loadStats()
    })
    
    return {
      stats,
      userChartRange,
      userChartData,
      userChartOption,
      getStatusLabel,
      activityTypeData,
      recentActivities,
      recentUsers,
      maxUserValue,
      getStatusType,
      loadStats,
      watchChartRange
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

/* 统计卡片样式 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-icon {
  font-size: 32px;
  margin-right: 16px;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #67C23A;
}

/* 图表区域样式 */
.charts-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-container {
  height: 300px;
}

.chart-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-content {
  height: 100%;
  width: 100%;
}

.mock-chart-labels {
  display: flex;
  justify-content: space-around;
  font-size: 12px;
  color: #909399;
}

.type-distribution {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.type-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}

.type-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.type-name {
  font-size: 14px;
  color: #606266;
}

.type-value {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.type-percent {
  font-size: 12px;
  color: #909399;
}

/* 最近活动样式 */
.recent-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.recent-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.recent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.recent-content {
  max-height: 400px;
  overflow-y: auto;
}

.recent-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;
}

.recent-item:last-child {
  border-bottom: none;
}

.item-title {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.item-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #909399;
}

.avatar-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 2px;
}

.user-meta {
  font-size: 12px;
  color: #909399;
}

.empty-recent {
  padding: 40px 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
  
  .recent-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .status-grid {
    grid-template-columns: 1fr;
  }
  
  .recent-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .item-meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style>